import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Bell, Check, Trash2, Plus } from "lucide-react";

export function RemindersPanel({ onOpenBooking, onOpenQuote, filter: filterProp, setFilter: setFilterProp, overdueOnly: overdueProp, setOverdueOnly: setOverdueProp }) {
  const [items, setItems] = useState([]);
  const [filterState, setFilterState] = useState("open");
  const filter = filterProp ?? filterState;
  const setFilter = setFilterProp ?? setFilterState;
  const [overdueState, setOverdueState] = useState(false);
  const overdueOnly = overdueProp ?? overdueState;
  const setOverdueOnly = setOverdueProp ?? setOverdueState;
  const [form, setForm] = useState({ title: "", due_date: "", reference: "", assigned_to: "", note: "" });
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get(`/admin/reminders?status=${filter}`).then(r => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const add = async () => {
    if (!form.title.trim()) { toast.error("Add a reminder title"); return; }
    try {
      await api.post("/admin/reminders", form);
      toast.success("Reminder added");
      setForm({ title: "", due_date: "", reference: "", assigned_to: "", note: "" });
      setShowForm(false);
      setFilter("open"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  const toggle = async (r) => {
    await api.patch(`/admin/reminders/${r.id}`, { status: r.status === "open" ? "done" : "open" });
    load();
  };
  const del = async (id) => { await api.delete(`/admin/reminders/${id}`); load(); };
  const snooze = async (r, days) => {
    const d = new Date(); d.setDate(d.getDate() + days);
    await api.patch(`/admin/reminders/${r.id}`, { due_date: d.toISOString().slice(0, 10) });
    toast.success(days === 1 ? "Snoozed to tomorrow" : `Snoozed ${days} days`);
    load();
  };
  const refreshAuto = async () => {
    await api.post("/admin/reminders/refresh-auto");
    toast.success("Refreshed auto reminders");
    load();
  };

  const isOverdue = (d) => d && new Date(d) < new Date(new Date().toDateString());
  const visible = overdueOnly ? items.filter(r => r.status === "open" && isOverdue(r.due_date)) : items;

  return (
    <div data-testid="reminders-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2 items-center flex-wrap">
          {["open", "done"].map(f => (
            <button key={f} data-testid={`reminders-filter-${f}`} onClick={() => { setFilter(f); if (f !== "open") setOverdueOnly(false); }}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border ${filter === f ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] bg-white"}`}>{f}</button>
          ))}
          <button data-testid="reminders-filter-overdue" onClick={() => { setFilter("open"); setOverdueOnly(v => !v); }}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border ${overdueOnly ? "bg-[#C0392B] text-white border-[#C0392B]" : "border-[#E5E7EB] bg-white"}`}>
            Overdue only
          </button>
        </div>
        <div className="flex gap-2">
          <button data-testid="reminders-refresh-auto" onClick={refreshAuto} className="de-btn-secondary text-xs">Refresh</button>
          <button data-testid="reminders-add-toggle" onClick={() => setShowForm(s => !s)} className="de-btn-secondary"><Plus size={16} /> New reminder</button>
        </div>
      </div>

      {showForm && (
        <div className="border border-[#E5E7EB] bg-white p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reminder-form">
          <input data-testid="reminder-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="What needs doing?" className="border border-[#E5E7EB] px-3 py-2 text-sm md:col-span-2" />
          <div><div className="label-eyebrow mb-1">Due date</div><input data-testid="reminder-due" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
          <div><div className="label-eyebrow mb-1">Client ref (optional)</div><input data-testid="reminder-ref" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value.toUpperCase() })} placeholder="TEW-XXXX" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
          <div><div className="label-eyebrow mb-1">Assign to (email, optional)</div><input data-testid="reminder-assign" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })} placeholder="staff@esskayhomes.co.uk" className="border border-[#E5E7EB] px-3 py-2 text-sm w-full" /></div>
          <input data-testid="reminder-note" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" className="border border-[#E5E7EB] px-3 py-2 text-sm" />
          <button data-testid="reminder-save" onClick={add} className="de-btn-primary md:col-span-2 justify-center">Save reminder</button>
        </div>
      )}

      <div className="space-y-2">
        {visible.map(r => (
          <div key={r.id} data-testid={`reminder-${r.id}`} className="border border-[#E5E7EB] bg-white p-4 flex items-start gap-3">
            <button data-testid={`reminder-toggle-${r.id}`} onClick={() => toggle(r)} className={`mt-0.5 w-5 h-5 border flex items-center justify-center shrink-0 ${r.status === "done" ? "bg-[#C79A3B] border-[#C79A3B]" : "border-[#12263A] bg-white"}`}>{r.status === "done" && <Check size={12} className="text-white" />}</button>
            <div className="flex-1">
              <div className={`font-bold ${r.status === "done" ? "line-through text-[#6B7280]" : ""}`}>
                {r.title}
                {r.system && <span data-testid="reminder-auto-tag" className="ml-2 text-[9px] font-bold uppercase tracking-widest bg-[#12263A] text-white px-1.5 py-0.5 rounded">Auto</span>}
              </div>
              <div className="text-xs text-[#6B7280] mt-0.5 flex flex-wrap gap-x-3">
                {r.due_date && <span className={isOverdue(r.due_date) && r.status === "open" ? "text-[#C79A3B] font-bold" : ""}>Due {new Date(r.due_date).toLocaleDateString("en-GB")}</span>}
                {r.reference && <button onClick={() => (r.quote_id && onOpenQuote) ? onOpenQuote(r.quote_id) : onOpenBooking?.(r.reference)} className="underline hover:text-[#C79A3B]">{r.reference}{r.client_name ? ` · ${r.client_name}` : ""}</button>}
                {r.assigned_to && <span>→ {r.assigned_to}</span>}
              </div>
              {r.note && <div className="text-xs text-[#6B7280] mt-1">{r.note}</div>}
            </div>
            {r.status === "open" && (
              <select data-testid={`reminder-snooze-${r.id}`} defaultValue="" onChange={e => { const v = e.target.value; if (v) { snooze(r, Number(v)); e.target.value = ""; } }}
                className="border border-[#E5E7EB] bg-white text-xs px-1 py-1 shrink-0">
                <option value="">Snooze…</option>
                <option value="1">Tomorrow</option>
                <option value="3">In 3 days</option>
                <option value="7">Next week</option>
              </select>
            )}
            <button data-testid={`reminder-delete-${r.id}`} onClick={() => del(r.id)} className="text-[#6B7280] hover:text-[#C79A3B]"><Trash2 size={16} /></button>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="border border-dashed border-[#E5E7EB] p-10 text-center text-[#6B7280]">
            <Bell size={24} className="mx-auto mb-3 opacity-50" />
            {overdueOnly ? "Nothing overdue — great work." : `No ${filter} reminders.`}
          </div>
        )}
      </div>
    </div>
  );
}
